import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, fdt } from "../../api";
import InfoTip from "../../components/InfoTip";
import { StatusPill } from "../../components/bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const inputCls = "cr-input !text-xs !py-2 !rounded-xl";
const btnBlack = "bg-black text-white text-xs font-medium px-3.5 py-2 rounded-lg hover:bg-zinc-800 transition-colors";
const btnAccent = "bg-[#0A0A0A] text-white text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-[#26272A] transition-colors";
const btnOutline = "border border-zinc-300 text-xs font-medium px-3.5 py-2 rounded-lg hover:bg-zinc-50 transition-colors";

const SOURCE_TYPES = ["timocom", "trans_eu", "partner", "tms_erp", "api", "csv", "email", "other"];

const CONNECTOR_STATES = ["not_connected", "pending_approval", "free", "trial", "paid", "connected", "error"];

function ConnectorsSection() {
  const [connectors, setConnectors] = useState([]);
  const [sources, setSources] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/connectors").then((r) => setConnectors(r.data.connectors)).catch(() => {});
    api.get("/admin/sources").then((r) => setSources(r.data.sources)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const setState = async (key, status) => {
    let confirm = false;
    if (status === "paid") {
      confirm = window.confirm("This marks the integration as a PAID subscription. Confirm you approve the cost.");
      if (!confirm) return;
    }
    try {
      await api.post(`/admin/connectors/${key}/state`, { status, confirm });
      toast.success("Connector updated"); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const link = async (key, source_id) => {
    try {
      await api.post(`/admin/connectors/${key}/link`, { source_id: source_id || null });
      toast.success(source_id ? "Source linked" : "Unlinked"); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="mb-8" data-testid="connectors-section">
      <h3 className="text-sm font-bold mb-1 flex items-center">Integrations & external job sources
        <InfoTip text="A connector only shows 'Connected' after a real authorised credential has successfully imported jobs. Paid integrations require your explicit approval. Source names are never shown publicly." />
      </h3>
      <p className="text-xs text-zinc-400 mb-4">Owner approval is required before any paid integration is activated.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {connectors.map((c) => (
          <div key={c.key} data-testid={`connector-${c.key}`} className="bg-white border border-black/5 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold">{c.name}</p>
              <StatusPill status={c.status} />
            </div>
            <p className="text-[11px] text-zinc-400 mb-3">{c.access}</p>
            <div className="flex flex-wrap gap-2">
              <select data-testid={`connector-state-${c.key}`} value={c.status} onChange={(e) => setState(c.key, e.target.value)} className={inputCls}>
                {CONNECTOR_STATES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              <select data-testid={`connector-link-${c.key}`} value={c.source_id || ""} onChange={(e) => link(c.key, e.target.value || null)} className={inputCls}>
                <option value="">No source linked</option>
                {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SourcesSection() {
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ name: "", type: "timocom", auth_type: "none", api_key: "", access_token: "", pull_url: "", status_webhook_url: "", webhook_key: "", auto_publish: false, active: true, notes: "" });
  const load = useCallback(() => {
    api.get("/admin/sources").then((r) => setSources(r.data.sources)).catch(() => {});
    api.get("/admin/sync-logs").then((r) => setLogs(r.data.logs)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    const credentials = {};
    if (form.api_key) credentials.api_key = form.api_key;
    if (form.access_token) credentials.access_token = form.access_token;
    try {
      await api.post("/admin/sources", {
        name: form.name, type: form.type, auth_type: form.auth_type, credentials,
        pull_url: form.pull_url || null, status_webhook_url: form.status_webhook_url || null,
        webhook_key: form.webhook_key || null, auto_publish: form.auto_publish, active: form.active, notes: form.notes || null,
      });
      toast.success("Source connected"); setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="mb-8" data-testid="sources-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold flex items-center">Connected sources<InfoTip text="Authorised feeds: TIMOCOM, Trans.eu, partner TMS/ERP. Pull on schedule, receive webhooks, or push via API. Imported jobs go through the approval queue unless auto-publish is on." /></h3>
        <button data-testid="add-source-btn" onClick={() => setOpen(true)} className={btnAccent}>Connect source</button>
      </div>
      {sources.length === 0 && (
        <p className="text-xs text-zinc-400 bg-white border border-dashed border-zinc-200 rounded-xl p-6 text-center">
          No external sources connected yet. Connect TIMOCOM, Trans.eu or a TMS/ERP to start importing real jobs.
        </p>
      )}
      <div className="space-y-2">
        {sources.map((s) => (
          <div key={s.id} data-testid={`source-${s.id}`} className="bg-white border border-black/5 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">{s.name} <span className="text-[10px] uppercase tracking-wide text-zinc-400 ml-1">{s.type}</span></p>
              <p className="text-[11px] text-zinc-500">
                {s.pull_url ? `Pull: ${s.pull_url}` : "Push / webhook only"} · {s.has_credentials ? "credentials stored" : "no credentials"} · {s.auto_publish ? "auto-publish on" : "manual approval"}
                {s.last_sync_at && ` · last sync ${fdt(s.last_sync_at)}`}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <StatusPill status={s.active ? "active" : "suspended"} />
              {s.pull_url && <button data-testid={`sync-source-${s.id}`} onClick={async () => { try { const { data } = await api.post(`/admin/sources/${s.id}/sync`); toast.success(`Synced ${data.rows} rows into approval queue`); load(); } catch (e) { toast.error(fmtErr(e)); } }} className={btnBlack}>Sync now</button>}
              <button data-testid={`delete-source-${s.id}`} onClick={async () => { await api.delete(`/admin/sources/${s.id}`); load(); }} className={btnOutline}>Remove</button>
            </div>
          </div>
        ))}
      </div>

      <h4 className="text-xs font-semibold text-[#78756E] uppercase tracking-wide mt-5 mb-2">Sync & error log</h4>
      <div className="bg-white border border-black/5 rounded-xl divide-y divide-zinc-50 max-h-48 overflow-y-auto">
        {logs.length === 0 && <p className="text-xs text-zinc-400 p-4">No sync activity yet.</p>}
        {logs.slice(0, 30).map((l) => (
          <div key={l.id} className="px-4 py-2 text-xs flex flex-wrap gap-x-3">
            <span className="font-mono text-zinc-400">{fdt(l.at)}</span>
            <span className="font-semibold">{l.direction}</span>
            {l.rows != null && <span>{l.rows} rows</span>}
            {l.job_ref && <span className="font-mono">{l.job_ref}</span>}
            {l.error && <span className="text-red-600">{l.error}</span>}
            {l.status_code && <span className="text-zinc-500">HTTP {l.status_code}</span>}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="source-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Connect external source</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input data-testid="source-name" placeholder="Name (e.g. TIMOCOM Production)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} w-full`} />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="source-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ").toUpperCase()}</option>)}
              </select>
              <select data-testid="source-auth" value={form.auth_type} onChange={(e) => setForm({ ...form, auth_type: e.target.value })} className={inputCls}>
                <option value="none">No auth</option><option value="api_key">API key</option>
                <option value="oauth2">OAuth2 token</option><option value="basic">Basic</option>
              </select>
            </div>
            {form.auth_type === "api_key" && <input data-testid="source-apikey" type="password" placeholder="API key" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className={`${inputCls} w-full`} />}
            {form.auth_type === "oauth2" && <input data-testid="source-token" type="password" placeholder="OAuth2 access token" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} className={`${inputCls} w-full`} />}
            <input data-testid="source-pull-url" placeholder="Pull URL (GET → JSON list of jobs, optional)" value={form.pull_url} onChange={(e) => setForm({ ...form, pull_url: e.target.value })} className={`${inputCls} w-full`} />
            <input data-testid="source-status-url" placeholder="Status push-back URL (we POST status updates here)" value={form.status_webhook_url} onChange={(e) => setForm({ ...form, status_webhook_url: e.target.value })} className={`${inputCls} w-full`} />
            <input data-testid="source-webhook-key" placeholder="Webhook secret (they must send X-Source-Key)" value={form.webhook_key} onChange={(e) => setForm({ ...form, webhook_key: e.target.value })} className={`${inputCls} w-full`} />
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="source-autopublish" checked={form.auto_publish} onChange={(e) => setForm({ ...form, auto_publish: e.target.checked })} className="accent-[#1C1B1A]" />Auto-publish approved rows to the marketplace</label>
            <textarea data-testid="source-notes" placeholder="Notes (contact, contract ref…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={`${inputCls} w-full`} />
            <button data-testid="source-save-btn" onClick={save} disabled={!form.name} className={`${btnAccent} w-full py-2.5 disabled:opacity-40`}>Connect</button>
            <p className="text-[11px] text-zinc-400">Inbound webhook endpoint: POST {process.env.REACT_APP_BACKEND_URL}/api/intake/webhook/{"{source_id}"} · Email-to-job: POST /api/intake/email</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportDetail({ batchId, onDone }) {
  const [detail, setDetail] = useState(null);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState([]);
  const [rowFilter, setRowFilter] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/imports/${batchId}`);
    setDetail(data);
    const r = await api.get(`/admin/imports/${batchId}/rows`, { params: { status: rowFilter || undefined } });
    setRows(r.data.rows);
    api.get("/admin/users", { params: { role: "company" } }).then((x) => setCompanies(x.data.users)).catch(() => {});
  }, [batchId, rowFilter]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  if (!detail) return null;
  const mapped = detail.batch.status !== "pending";

  const applyMapping = async () => {
    try {
      const { data } = await api.post(`/admin/imports/${batchId}/mapping`, { mapping, company_id: companyId || null });
      toast.success(`Validated: ${data.validated} · duplicates: ${data.duplicates} · invalid: ${data.invalid}`);
      load(); onDone();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const approveAll = async () => {
    try {
      const { data } = await api.post(`/admin/imports/${batchId}/approve-all`);
      toast.success(`${data.created} jobs created and published`); load(); onDone();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="import-detail">
      <p className="text-xs text-zinc-500 mb-3">{detail.batch.filename || detail.batch.source} · {detail.batch.total_rows} rows · <StatusPill status={detail.batch.status} /></p>
      {!mapped && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 mb-4">
          <h4 className="text-xs font-bold mb-2 flex items-center">Field mapping<InfoTip text="Map your file's columns to CourierRadar job fields. Rows missing pickup or delivery fail validation; duplicates are detected against existing jobs." /></h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {detail.mappable_fields.map((f) => (
              <label key={f} className="text-[10px] font-semibold text-zinc-500 uppercase">{f.replace(/_/g, " ")}
                <select data-testid={`map-${f}`} value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })} className={`${inputCls} w-full mt-0.5`}>
                  <option value="">—</option>
                  {detail.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <select data-testid="import-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className={inputCls}>
              <option value="">Owner: platform (no company)</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.company_name || c.name}</option>)}
            </select>
            <button data-testid="apply-mapping-btn" onClick={applyMapping} className={btnBlack}>Validate rows</button>
          </div>
        </div>
      )}
      {mapped && (
        <div className="flex items-center gap-2 mb-3">
          <select data-testid="row-filter" value={rowFilter} onChange={(e) => setRowFilter(e.target.value)} className={inputCls}>
            <option value="">All rows</option><option value="validated">Validated</option><option value="invalid">Invalid</option>
            <option value="duplicate">Duplicates</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
          </select>
          <button data-testid="approve-all-btn" onClick={approveAll} className={btnAccent}>Approve all validated</button>
        </div>
      )}
      <div className="space-y-2 max-h-[50vh] overflow-y-auto">
        {rows.map((r) => (
          <div key={r.id} data-testid={`import-row-${r.id}`} className="bg-white border border-black/5 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs min-w-0">
              <p className="font-semibold truncate">{r.mapped ? `${r.mapped.pickup_address || r.mapped.pickup_city || "?"} → ${r.mapped.delivery_address || r.mapped.delivery_city || "?"}` : (r.raw.subject || JSON.stringify(r.raw).slice(0, 80))}</p>
              {r.errors?.length > 0 && <p className="text-red-600 text-[11px]">{r.errors.join(" · ")}</p>}
              {r.job_id && <a href={`/jobs/${r.job_id}`} className="text-[#0A0A0A] text-[11px]">→ job created</a>}
            </div>
            <div className="flex items-center gap-2">
              <StatusPill status={r.status} />
              {r.status === "validated" && <>
                <button data-testid={`approve-row-${r.id}`} onClick={async () => { try { await api.post(`/admin/import-rows/${r.id}/approve`); load(); } catch (e) { toast.error(fmtErr(e)); } }} className={btnBlack}>Approve</button>
                <button data-testid={`reject-row-${r.id}`} onClick={async () => { await api.post(`/admin/import-rows/${r.id}/reject`); load(); }} className={btnOutline}>Reject</button>
              </>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-xs text-zinc-400 py-6 text-center">No rows with this status.</p>}
      </div>
    </div>
  );
}

export function ImportsTab() {
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const load = useCallback(() => { api.get("/admin/imports").then((r) => setBatches(r.data.batches)).catch(() => {}); }, []);
  useEffect(load, [load]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/imports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${data.total_rows} rows`); setActiveBatch(data.id); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="ct-imports">
      <ConnectorsSection />
      <SourcesSection />
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold flex items-center">Import batches<InfoTip text="CSV/XLSX uploads, source pulls, webhooks and email-to-job all land here. Map fields, validate, then approve to create real jobs." /></h3>
        <label className={`${btnAccent} cursor-pointer`}>
          Upload CSV/XLSX
          <input data-testid="import-upload-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
      </div>
      {batches.length === 0 && <p className="text-xs text-zinc-400 bg-white border border-dashed border-zinc-200 rounded-xl p-6 text-center">No imports yet. Upload a file or connect a source above.</p>}
      <div className="space-y-2 mb-6">
        {batches.map((b) => (
          <button key={b.id} data-testid={`batch-${b.id}`} onClick={() => setActiveBatch(b.id === activeBatch ? null : b.id)}
            className={`w-full text-left bg-white border rounded-xl p-4 transition-all ${activeBatch === b.id ? "border-[#0A0A0A] ring-2 ring-[#0A0A0A]/20" : "border-black/5 hover:border-black/15"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold">{b.filename || `${b.source} intake`} <span className="text-[11px] text-zinc-400 font-normal">· {b.total_rows} rows · {fdt(b.created_at)}</span></p>
              <div className="flex gap-1.5">
                {Object.entries(b.counts || {}).map(([s, n]) => <span key={s} className="text-[10px] font-semibold text-zinc-500 bg-zinc-100 rounded-full px-2 py-0.5">{s}: {n}</span>)}
                <StatusPill status={b.status} />
              </div>
            </div>
          </button>
        ))}
      </div>
      {activeBatch && <ImportDetail batchId={activeBatch} onDone={load} />}
    </div>
  );
}
